import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layers, Plus, Search, Edit3, Copy as CopyIcon, Archive, MoreVertical, ShieldCheck } from "lucide-react";
import AppShell from "../components/app/AppShell";
import PageHeader, { StatusPill, EmptyState } from "../components/app/PageHeader";
import api from "../lib/api";
import { track, EVT } from "../lib/analytics";

export default function Flows() {
  const nav = useNavigate();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (q) params.set("q", q);
      const { data } = await api.get(`/api/flows?${params}`);
      setFlows(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const createFlow = async (template) => {
    setCreating(true);
    try {
      const { data } = await api.post("/api/flows", { name: "Untitled flow", template });
      track(EVT.flow_created, { template: template || "blank" });
      nav(`/app/flows/${data.id}/edit`);
    } finally { setCreating(false); }
  };

  return (
    <AppShell>
      <PageHeader
        title="Onboarding flows"
        subtitle="Design branded client journeys with compliance built in."
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => createFlow("ifa")} data-testid="new-template">Browse templates</button>
            <button className="btn btn-primary" onClick={() => createFlow(null)} disabled={creating} data-testid="new-flow"><Plus size={16} />Create flow</button>
          </>
        }
      />

      <div className="card2 p-4 mb-5 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate2" />
          <input className="input2 pl-9" placeholder="Search flows…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="flows-search" />
        </div>
        <select className="input2 md:w-48" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="flows-status-filter">
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-16" />)}</div>
      ) : flows.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="No flows yet"
          description="Create your first onboarding flow from a template or from scratch."
          action={<button onClick={() => createFlow("ifa")} className="btn btn-primary">Start from a template</button>}
        />
      ) : (
        <div className="card2 overflow-hidden">
          <table className="w-full" data-testid="flows-table">
            <thead className="bg-surface border-b border-border2">
              <tr className="text-left">
                <th className="px-5 py-3 t-label text-slate2">Name</th>
                <th className="px-5 py-3 t-label text-slate2">Status</th>
                <th className="px-5 py-3 t-label text-slate2">Steps</th>
                <th className="px-5 py-3 t-label text-slate2">Last scan</th>
                <th className="px-5 py-3 t-label text-slate2">Updated</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border2">
              {flows.map((f) => (
                <tr key={f.id} className="hover:bg-surface" data-testid={`flow-row-${f.id}`}>
                  <td className="px-5 py-4">
                    <Link to={`/app/flows/${f.id}/edit`} className="t-body font-medium text-ink hover:text-indigo2">{f.name}</Link>
                    <div className="t-caption">by {f.created_by_name}</div>
                  </td>
                  <td className="px-5 py-4"><StatusPill status={f.status} /></td>
                  <td className="px-5 py-4 t-mono">{f.step_count}</td>
                  <td className="px-5 py-4">
                    {f.last_scan ? (
                      <span className={`badge2 ${f.last_scan.verdict === "pass" ? "badge-verified" : f.last_scan.verdict === "review" ? "badge-pending" : "badge-failed"}`}>
                        {f.last_scan.score}/100
                      </span>
                    ) : <span className="t-caption">Not scanned</span>}
                  </td>
                  <td className="px-5 py-4 t-caption">{f.updated_at ? new Date(f.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="px-5 py-4 text-right">
                    <Link to={`/app/flows/${f.id}/edit`} className="btn btn-ghost btn-sm"><Edit3 size={14} />Edit</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
